import { getAllBackordersThunk } from '@redux/features/backorder.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import { dateFormatter } from 'helpers/formatter_helper';

const Backorders: FC = () => {
    const [page, setPage] = useState<number>(1);
    const [filterStatus, setFilterStatus] = useState<string>('');

    const dispatch = useAppDispatch();
    const { backorders, loading, pagination } = useAppSelector(
        (state: RootState) => state.backorderState
    );

    const changePage = (p: string) => {
        setPage(Number(p));
    };

    useEffect(() => {
        const params: any = {
            pageNumber: page - 1,
            pageSize: 20,
        };

        if (filterStatus && filterStatus.trim() !== '') {
            params.status = filterStatus.trim();
        }

        dispatch(getAllBackordersThunk(params));
    }, [page, filterStatus, dispatch]);

    const formatStatus = (status: string | null | undefined) => {
        if (!status) return '-';
        return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const getStatusColor = (status: string | null | undefined) => {
        if (!status) return 'bg-gray-100 text-gray-800';
        switch (status.toUpperCase()) {
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800';
            case 'FULFILLED':
                return 'bg-green-100 text-green-800';
            case 'PARTIAL':
                return 'bg-yellow-100 text-yellow-800';
            case 'CANCELLED':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <React.Fragment>
            <BreadCrumb title="Backorders" pageTitle="Backorders" />
            <section className="py-5">
                <div className="p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between mb-4">
                        <h3 className="font-extrabold text-[#bd902e]">Backorders</h3>
                    </div>
                    {/* Filters */}
                    <div className="mb-4 flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
                            <select
                                value={filterStatus}
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    setPage(1);
                                }}
                                className="form-select border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                            >
                                <option value="">All Statuses</option>
                                <option value="PENDING">Pending</option>
                                <option value="PARTIAL">Partial</option>
                                <option value="FULFILLED">Fulfilled</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Order ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">NDC Code</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Remaining</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Created Date</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={backorders?.length ?? 0} animationKey={`backorders-${page}`}>
                                            {backorders && backorders.length > 0 ? (
                                                backorders.map((backorder: any) => (
                                                    <AnimatedTableRow
                                                        rowKey={backorder.id}
                                                        detailRecord={backorder}
                                                        className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                    >
                                                        <td className="px-3.5 py-2.5 font-mono">{backorder.id || '-'}</td>
                                                        <td className="px-3.5 py-2.5 font-mono">
                                                            {backorder.order?.id || '-'}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {backorder.product?.itemName || '-'}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {backorder.product?.ndcCode || '-'}
                                                        </td>
                                                        <td className="px-3.5 py-2.5 text-center">
                                                            {backorder.quantity || '-'}
                                                        </td>
                                                        <td className="px-3.5 py-2.5 text-center">
                                                            {backorder.remainingQuantity || '-'}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            <span
                                                                className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
                                                                    backorder.status
                                                                )}`}
                                                            >
                                                                {formatStatus(backorder.status)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {dateFormatter(backorder.createdAt) || '-'}
                                                        </td>
                                                    </AnimatedTableRow>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={8} className="px-3.5 py-2.5 text-center">
                                                        No backorders found
                                                    </td>
                                                </tr>
                                            )}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                {/* Pagination */}
                                {pagination && pagination.totalPages > 0 && (
                                    <Pagination
                                        perPageData={pagination?.pageSize || 20}
                                        total={pagination?.totalElements || 0}
                                        currentPage={page}
                                        changePage={changePage}
                                        length={backorders?.length || 0}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </React.Fragment>
    );
};

export default Backorders;

